import {
  ArrowUpRight,
  Download,
  Github,
  Linkedin,
  Mail,
  Phone,
} from "lucide-react";

import { ContentLink } from "@/components/core/content-link";
import { Card } from "@/components/ui/card";
import type { ContactHub } from "@/types/site";

interface ContactHubSectionProps {
  content: ContactHub;
}

function getMethodIcon(href: string): JSX.Element {
  if (href.startsWith("mailto:")) {
    return <Mail aria-hidden="true" className="size-5 text-primary" />;
  }

  if (href.startsWith("tel:")) {
    return <Phone aria-hidden="true" className="size-5 text-primary" />;
  }

  if (href.includes("github.com")) {
    return <Github aria-hidden="true" className="size-5 text-primary" />;
  }

  if (href.includes("linkedin.com")) {
    return <Linkedin aria-hidden="true" className="size-5 text-primary" />;
  }

  return <ArrowUpRight aria-hidden="true" className="size-5 text-primary" />;
}

function getMethodActionLabel(href: string): string {
  if (href.startsWith("mailto:")) return "Send email";
  if (href.startsWith("tel:")) return "Call";
  if (href.includes("linkedin.com")) return "Open LinkedIn";
  if (href.includes("github.com")) return "Open GitHub";
  return "Open";
}

export function ContactHubSection({
  content,
}: ContactHubSectionProps): JSX.Element {
  const primaryMethod = content.methods.find((method) =>
    method.href.startsWith("mailto:"),
  );
  const secondaryMethods = content.methods.filter(
    (method) => method !== primaryMethod,
  );

  return (
    <section className="overflow-x-clip pb-20">
      <div className="mx-auto grid min-w-0 max-w-6xl gap-4 px-4 sm:gap-6 sm:px-6 lg:grid-cols-[minmax(0,1.15fr),minmax(0,0.85fr)] lg:px-10">
        {primaryMethod ? (
          <Card className="min-w-0 border border-primary/20 bg-[linear-gradient(180deg,rgba(26,28,28,0.96),rgba(18,20,20,0.98))] p-6 sm:p-8 md:p-10">
            <div className="flex min-w-0 h-full flex-col justify-between gap-8 sm:gap-10">
              <div className="space-y-5 sm:space-y-6">
                <div className="flex size-11 items-center justify-center rounded-full-pill bg-primary text-primary-foreground sm:size-12">
                  <Mail
                    aria-hidden="true"
                    className="size-5"
                  />
                </div>
                <div className="space-y-2 sm:space-y-3">
                  <p className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-primary/90 sm:text-xs sm:tracking-[0.24em]">
                    {primaryMethod.label}
                  </p>
                  {primaryMethod.showValue !== false ? (
                    <p className="max-w-full break-all text-xl font-semibold leading-tight text-foreground sm:break-words sm:text-2xl md:text-4xl">
                      {primaryMethod.value}
                    </p>
                  ) : null}
                </div>
              </div>
              <ContentLink
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full-pill bg-primary px-5 py-3 text-center text-sm font-bold uppercase tracking-[0.16em] text-primary-foreground transition hover:bg-primary/92 sm:w-fit sm:justify-start sm:tracking-[0.18em]"
                href={primaryMethod.href}
              >
                {getMethodActionLabel(primaryMethod.href)}
                <ArrowUpRight
                  aria-hidden="true"
                  className="size-4"
                />
              </ContentLink>
            </div>
          </Card>
        ) : null}
        <div className="grid min-w-0 gap-4 sm:gap-5">
          {secondaryMethods.map((method) => (
            <Card
              className="min-w-0 border border-border/20 bg-spotify-surface/75 p-5 sm:p-6"
              key={`${method.label}-${method.value}`}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full-pill border border-border/20 bg-spotify-surface-light sm:size-11">
                  {getMethodIcon(method.href)}
                </div>
                <div className="min-w-0 flex-1 space-y-1.5 sm:space-y-2">
                  <div className="space-y-1">
                    <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em] text-spotify-silver sm:text-xs sm:tracking-[0.22em]">
                      {method.label}
                    </p>
                    {method.showValue !== false ? (
                      <p className="break-words text-base font-semibold leading-6 text-foreground sm:text-lg sm:leading-7">
                        {method.value}
                      </p>
                    ) : null}
                  </div>
                  <ContentLink
                    className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-primary transition hover:text-primary/80 sm:tracking-[0.18em]"
                    href={method.href}
                  >
                    {getMethodActionLabel(method.href)}
                    <ArrowUpRight
                      aria-hidden="true"
                      className="size-4"
                    />
                  </ContentLink>
                </div>
              </div>
            </Card>
          ))}
          <Card className="min-w-0 border border-primary/20 bg-primary/8 p-5 sm:p-6">
            <div className="flex h-full items-center gap-3 sm:gap-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full-pill bg-primary text-primary-foreground sm:size-11">
                <Download
                  aria-hidden="true"
                  className="size-5"
                />
              </div>
              <div className="min-w-0 flex-1 space-y-1.5 sm:space-y-2">
                <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em] text-primary/90 sm:text-xs sm:tracking-[0.22em]">
                  Resume
                </p>
                {content.resume ? (
                  <ContentLink
                    className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-primary transition hover:text-primary/80 sm:tracking-[0.18em]"
                    download={content.resume.download}
                    href={content.resume.href}
                  >
                    {content.resume.label}
                    <ArrowUpRight
                      aria-hidden="true"
                      className="size-4"
                    />
                  </ContentLink>
                ) : null}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
